import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWorkflowHistoriesRequest } from "../store/slices/workflows/workflowHistoriesSlice";
import * as em from "common/ErrorMessages";
import {
	deleteWorkflowProcessThunk,
	fetchProcessDocumentsThunk,
	fetchDocumentDownloadThunk,
	regenerateProcessDocumentThunk,
	fetchDocumentTemplateVersionsThunk,
	fetchReassignCandidatesThunk,
	reassignTaskThunk,
	cloneWorkflowProcessThunk,
	viewWorkflowProcessThunk,
} from "../store/thunks/workflowThunks";
import { toDDMMYYYY } from "helpers/dateUtils";
import { downloadFromUrl } from "helpers/downloadUtils";
import EnhancedConfirmationDialog from "../components/common/custom-component/EnhancedConfirmationDialog";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import Loader from "../components/common/custom-component/Loader";
import CustomPagination from "../components/common/CustomPagination";
import DataContainer from "../components/common/DataContainer/DataContainer";

import { CalendarToday as CalendarIcon } from "@mui/icons-material";
import { Alert, Box, Typography, Stack, TextField, MenuItem, Divider } from "@mui/material";

import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import SearchFilterBar from "components/common/SearchFilterBar";
import { useToast } from "../components/common/custom-component/ToastProvider";
import { useTableColumns } from "../hooks/useTableColumns";
import { useWorkflowAccess } from "../hooks/useWorkflowAccess";
import { createMyProcessesRequest, mapProcessDocumentResponse } from "../dto/workflowDto";
import { workflowHistoryReducer, initialState } from "../reducers/workflowHistoryReducer";
import { logger } from "helpers/logger";
import { getErrorMessage } from "helpers/errorUtils";

// sessionStorage key for the page number to return to after viewing a process.
const SAVED_PAGE_KEY = "workflowHistoryPageIndex";

export default function WorkflowHistoryPage() {
	//#region Hooks / Services
	const { success: ToastSuccess, error: ToastError, info: ToastInfo } = useToast();
	const [t] = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	//#endregion

	// #region State / Store
	// Use reducer for consolidated state management. The init function restores the page saved
	// before navigating away to view a process, then clears it right away so a later normal visit
	// starts at page 1. Page-specific key: other pages save their own.
	const [state, dispatchState] = useReducer(workflowHistoryReducer, initialState, (init) => {
		const savedPage = sessionStorage.getItem(SAVED_PAGE_KEY);
		sessionStorage.removeItem(SAVED_PAGE_KEY);
		return savedPage
			? { ...init, table: { ...init.table, currentPage: parseInt(savedPage, 10) || 1 } }
			: init;
	});
	const recordsPerPage = state.table.recordsPerPage || 10;
	const currentPage = state.table.currentPage;

	// One page of "My processes" ({ processes, totalCount }), already mapped by the saga
	// (mapMyProcessResponse). The backend pages, so nothing is sliced here.
	const { data: WorkflowHistoryData, loading, error } = useSelector((state) => ({
		data: state.workflowHistories?.data?.data,
		loading: state.workflowHistories?.loading,
		error: state.workflowHistories?.error,
	}));
	const displayData = useMemo(() => WorkflowHistoryData?.processes ?? [], [WorkflowHistoryData]);
	const totalCount = WorkflowHistoryData?.totalCount ?? 0;
	const totalPages = Math.max(1, Math.ceil(totalCount / recordsPerPage));
	// #endregion

	// #region Fetch Workflow History
	// Fetches from the current page/size/search/sort state. Handlers only change that state; the
	// effect below refetches whenever it changes. The backend takes the user from the token and
	// limits rows to the user's processes (started, or a task completed) of workflows allowed in
	// the selected company. Default order: latest activity first.
	const fetchMyProcesses = useCallback(() => {
		const request = createMyProcessesRequest({
			pageNumber: currentPage,
			recordsByPage: recordsPerPage,
			searchParam: state.table.searchParams.searchTerm,
			startDate: state.table.searchParams.startDate,
			endDate: state.table.searchParams.endDate,
			sortField: state.table.sortField,
			sortDirection: state.table.sortDirection,
		});
		// Search text and label sorts need the translated labels, so they go to the search endpoint.
		const needsLabels = !!request.searchParam || state.table.sortByLabel;

		dispatch(fetchWorkflowHistoriesRequest({ request, needsLabels }));
	}, [dispatch, currentPage, recordsPerPage, state.table.searchParams, state.table.sortField, state.table.sortDirection, state.table.sortByLabel]);

	// location.key: refetch when the user navigates to this page again (even from the same URL).
	useEffect(() => {
		fetchMyProcesses();
	}, [fetchMyProcesses, location.key]);

	// Based on whether a response is in the store, not on how many rows it has: an empty list is
	// still a loaded answer. Never loaded (or cleared by a company switch) -> loader in the table
	// area; loaded before -> keep showing it with a small refresh spinner.
	const hasLoaded = WorkflowHistoryData != null;
	const isFirstLoad = loading && !hasLoaded;
	const isBackgroundRefreshing = loading && hasLoaded;
	// #endregion

	// #region FILTERS & SORT
	const applyFilters = () => {
		dispatchState({
			type: "SET_SEARCH_PARAMS",
			payload: {
				searchTerm: state.table.searchInput?.trim() || "",
				startDate: toDDMMYYYY(state.table.startDateInput),
				endDate: toDDMMYYYY(state.table.endDateInput),
			},
		});
	};

	const clearFilters = () => {
		dispatchState({ type: "CLEAR_FILTERS" });
	};

	// DataTable calls onSort(columnKey) only. Same column flips asc/desc; a new column starts asc.
	// byLabel comes from the column config (sortByLabel), so no column names live in code.
	const handleSort = (column) => {
		if (!column) return;
		const direction = column === state.table.sortField && state.table.sortDirection === "asc" ? "desc" : "asc";
		const byLabel = columns.find((c) => c.key === column)?.sortByLabel === true;
		dispatchState({ type: "SET_SORT", payload: { field: column, direction, byLabel } });
	};

	const handleChangeRecordsPerPage = (e) => {
		dispatchState({ type: "SET_RECORDS_PER_PAGE", payload: Number(e.target.value) || 10 });
	};

	const handlePageChange = (event, value) => {
		dispatchState({ type: "SET_PAGE", payload: value });
	};
	// #endregion

	// #region REGENERATE PDF
	// Renders one of the process's documents again from its saved data, as a new version; the previous
	// file is kept. Two steps: pick the document (only when there are several), then the template
	// version to render it with (newest preselected; older versions stay selectable). Always asks
	// first because it changes what everyone downloads from then on. regenerate_pdf is checked by the
	// backend; the button's visibility comes from the column config.

	// Second step: load the document's template versions and ask which one to use.
	const openRegenerateVersions = useCallback(
		async (document) => {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
			try {
				const response = await dispatch(fetchDocumentTemplateVersionsThunk({ documentId: document.documentId })).unwrap();
				dispatchState({ type: "OPEN_REGENERATE_VERSION_DIALOG", payload: { document, versions: response?.data ?? [] } });
			} catch (error) {
				logger.error("Error fetching template versions:", error);
				ToastError(getErrorMessage(error, t("Failed to load the template versions")));
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[dispatch, t, ToastError]
	);

	const handleRegenerateClick = useCallback(
		async (row) => {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			let documents;
			try {
				const response = await dispatch(fetchProcessDocumentsThunk({ processInstanceId: row.process_instance_id })).unwrap();
				// Pending ones are still being rendered; failed ones are rendered again (a retry).
				documents = (response?.data ?? [])
					.map(mapProcessDocumentResponse)
					.filter((document) => document.status === "ready" || document.status === "failed");
			} catch (error) {
				logger.error("Error fetching process documents:", error);
				ToastError(getErrorMessage(error, t("Failed to load the documents")));
				return;
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}

			if (documents.length === 0) {
				ToastInfo(t("No document has been generated for this process yet"));
				return;
			}
			// One document: straight to the version choice.
			if (documents.length === 1) {
				await openRegenerateVersions(documents[0]);
				return;
			}
			dispatchState({ type: "OPEN_REGENERATE_DIALOG", payload: documents });
		},
		[dispatch, t, ToastError, ToastInfo, openRegenerateVersions]
	);

	const handleCancelRegenerate = () => {
		dispatchState({ type: "CLOSE_REGENERATE_DIALOG" });
	};

	// First step confirmed (a document picked): go on to its versions.
	const handleConfirmRegenerate = async (data) => {
		const document = state.dialogs.regenerateDocuments.find((d) => d.documentId === data?.documentId);
		dispatchState({ type: "CLOSE_REGENERATE_DIALOG" });
		if (document) {
			await openRegenerateVersions(document);
		}
	};

	const handleCancelRegenerateVersion = () => {
		dispatchState({ type: "CLOSE_REGENERATE_VERSION_DIALOG" });
	};

	// Second step confirmed: regenerate with the chosen version (none listed = the version it uses).
	const handleConfirmRegenerateVersion = async (data) => {
		const document = state.dialogs.regenerateDocument;
		dispatchState({ type: "CLOSE_REGENERATE_VERSION_DIALOG" });
		if (!document) return;

		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
		try {
			const response = await dispatch(regenerateProcessDocumentThunk({
				documentId: document.documentId,
				templateVersionId: data?.templateVersionId,
			})).unwrap();
			const version = response?.data?.version;
			ToastSuccess(version ? `${t("Document regenerated")} (${t("version")} ${version})` : t("Document regenerated"));
		} catch (error) {
			logger.error("Error regenerating document:", error);
			ToastError(getErrorMessage(error, t("Failed to regenerate the document")));
		} finally {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		}
	};

	// One radio option per document; the date and version tell apart documents with the same name.
	const regenerateFields = useMemo(() => [
		{
			name: "documentId",
			type: "radio",
			row: false,
			required: true,
			options: state.dialogs.regenerateDocuments.map((document) => {
				const date = document.versionCreatedAt || document.createdAt;
				const details = [date ? new Date(date).toLocaleString() : null, `${t("version")} ${document.version}`, document.status === "failed" ? t("failed") : null]
					.filter(Boolean)
					.join(", ");
				return { value: document.documentId, label: `${t(document.displayName)} (${details})` };
			}),
		},
	], [state.dialogs.regenerateDocuments, t]);

	// Newest document preselected.
	const regenerateDefaults = useMemo(() => ({
		documentId: state.dialogs.regenerateDocuments[state.dialogs.regenerateDocuments.length - 1]?.documentId ?? "",
	}), [state.dialogs.regenerateDocuments]);

	// One option per template version, newest first: its number and upload date, marked "latest" and
	// "used now" (the version the document was last rendered with). No versions recorded = no choice.
	const regenerateVersionFields = useMemo(() => {
		const versions = state.dialogs.regenerateVersions;
		if (versions.length === 0) return [];
		return [
			{
				name: "templateVersionId",
				label: t("Template version"),
				type: "dropdown",
				required: true,
				options: versions.map((version) => {
					const details = [
						version.uploadedAt ? new Date(version.uploadedAt).toLocaleString() : null,
						version.isLatest ? t("in use") : null,
						version.isCurrentForDocument ? t("used now") : null,
					].filter(Boolean).join(", ");
					return { value: version.templateId, label: `${t("version")} ${version.version}${details ? ` (${details})` : ""}` };
				}),
			},
		];
	}, [state.dialogs.regenerateVersions, t]);

	// The version in use preselected (isLatest = the version the template uses now).
	const regenerateVersionDefaults = useMemo(() => ({
		templateVersionId: state.dialogs.regenerateVersions.find((v) => v.isLatest)?.templateId ?? "",
	}), [state.dialogs.regenerateVersions]);
	// #endregion

	// #region REASSIGN TASK
	// Gives the process's reassignable task to another user: only a step marked reassignable in
	// authz_workflow_steps, and only once it has an assignee (the row's can_reassign, computed by the
	// backend with the same rule). The backend lists who can take it (company users who could execute
	// it: execute_workflow on the workflow and access to the task's form) and checks reassign_task for
	// the caller; the button's visibility comes from the column config.
	const handleReassignClick = useCallback(
		async (row) => {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			let reassign;
			try {
				const response = await dispatch(fetchReassignCandidatesThunk({ processInstanceId: row.process_instance_id })).unwrap();
				reassign = response?.data;
			} catch (error) {
				logger.error("Error fetching reassign candidates:", error);
				ToastError(getErrorMessage(error, t("Failed to load who the task can be reassigned to")));
				return;
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}

			if (!reassign?.candidates?.length) {
				ToastInfo(t("No other user can work on this task"));
				return;
			}
			dispatchState({ type: "OPEN_REASSIGN_DIALOG", payload: reassign });
		},
		[dispatch, t, ToastError, ToastInfo]
	);

	const handleCancelReassign = () => {
		dispatchState({ type: "CLOSE_REASSIGN_DIALOG" });
	};

	const handleConfirmReassign = async (data) => {
		const taskId = state.dialogs.reassign?.taskId;
		dispatchState({ type: "CLOSE_REASSIGN_DIALOG" });
		if (!taskId || !data?.newAssignee) return;

		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
		try {
			await dispatch(reassignTaskThunk({ taskId, newAssignee: data.newAssignee })).unwrap();
			ToastSuccess(t("Task reassigned"));
			// The new assignee shows once the workflow sync has updated the task.
			fetchMyProcesses();
		} catch (error) {
			logger.error("Error reassigning task:", error);
			ToastError(getErrorMessage(error, t("Failed to reassign the task")));
		} finally {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		}
	};

	const reassignFields = useMemo(() => [
		{
			name: "newAssignee",
			label: t("Reassign to"),
			type: "select",
			required: true,
			errorMessage: t("Please select a user"),
			options: (state.dialogs.reassign?.candidates ?? []).map((user) => ({
				value: user.userId,
				label: user.fullName ? `${user.fullName} (${user.username})` : user.username,
			})),
		},
	], [state.dialogs.reassign, t]);

	const reassignDefaults = useMemo(() => ({ newAssignee: "" }), []);
	// #endregion

	// #region DELETE PROCESS
	// Deletes (cancels) the whole process instance with all its tasks, the same as on the Pending
	// Tasks page. The backend checks delete_workflow on the workflow in this company and the
	// workflow's delete policy (by default only the starter, while nobody has acted on it yet); the
	// row's can_delete flag applies the same rule, so the button only shows where it would succeed.
	const handleDeleteClick = useCallback((row) => {
		dispatchState({ type: "OPEN_DELETE_DIALOG", payload: { processInstanceId: row.process_instance_id } });
	}, []);

	const handleCancelDelete = () => {
		dispatchState({ type: "CLOSE_DELETE_DIALOG" });
	};

	const handleConfirmDelete = async () => {
		const processInstanceId = state.dialogs.selectedRecord?.processInstanceId;
		dispatchState({ type: "CLOSE_DELETE_DIALOG" });
		if (!processInstanceId) return;

		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
		try {
			await dispatch(deleteWorkflowProcessThunk({ processInstanceId })).unwrap();
			ToastSuccess(t(em.deleted_successfully));
			// The process is now terminated; the refreshed list shows its new state.
			fetchMyProcesses();
		} catch (error) {
			logger.error("Error deleting process:", error);
			ToastError(getErrorMessage(error, t(em.failed_delete_workflow)));
		} finally {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		}
	};
	// #endregion


	// #region CLONE WORKFLOW
	// Starts a new process of the same workflow, pre-filled from this one: the backend copies only the
	// fields marked clone = true in the form (Form.io custom property) and checks clone_workflow. The
	// new step opens like after Start; nothing is saved until the user submits.
	const handleCloneClick = useCallback((row) => {
		dispatchState({ type: "OPEN_CLONE_DIALOG", payload: { processInstanceId: row.process_instance_id } });
	}, []);

	const handleCancelClone = () => {
		dispatchState({ type: "CLOSE_CLONE_DIALOG" });
	};

	const handleConfirmClone = async () => {
		const processInstanceId = state.dialogs.selectedRecord?.processInstanceId;
		dispatchState({ type: "CLOSE_CLONE_DIALOG" });
		if (!processInstanceId) return;

		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
		try {
			const response = await dispatch(cloneWorkflowProcessThunk({ processInstanceId })).unwrap();
			const clone = response?.data;

			// Started, but its first step isn't a form waiting for this user (e.g. a service step first).
			if (!clone?.formId || !clone?.taskId) {
				ToastInfo(t("The new process was started"));
				fetchMyProcesses();
				return;
			}

			navigate("/formRenderer", {
				state: {
					submissions: [
						{
							formId: clone.formId,
							submissionId: null,
							processInstanceId: clone.processInstanceId,
							taskId: clone.taskId,
							data: clone.data ?? {},
						},
					],
					taskId: clone.taskId,
					readOnly: false,
					title: t("Clone Workflow"),
				},
			});
		} catch (error) {
			logger.error("Error cloning process:", error);
			ToastError(getErrorMessage(error, t("Failed to clone the process")));
		} finally {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		}
	};
	// #endregion

	// #region VIEW PROCESS
	// Opens the process read-only in FormRendererPage. The backend decides everything (the process is
	// one of the caller's, the workflow is allowed in the company, and which forms the caller's roles
	// grant), so only the process is sent.
	const handleView = useCallback(
		async (processInstanceId) => {
			if (!processInstanceId) {
				logger.error("No processInstanceId provided to handleView");
				return;
			}

			dispatchState({
				type: "SET_LOADING",
				payload: { key: "isLoadingBackdrop", value: true },
			});

			try {
				const result = await dispatch(viewWorkflowProcessThunk({ processInstanceId })).unwrap();

				if (result?.data) {
					sessionStorage.setItem(SAVED_PAGE_KEY, currentPage);
					navigate("/formRenderer", {
						state: {
							...result.data,
							title: t("View History"),
						},
					});
				} else {
					ToastError(result?.message || t("Failed to load workflow history forms"));
				}
			} catch (error) {
				logger.error("Error in handleView:", error);
				ToastError(getErrorMessage(error, t("Failed to load workflow history forms")));
			} finally {
				dispatchState({
					type: "SET_LOADING",
					payload: { key: "isLoadingBackdrop", value: false },
				});
			}
		},
		[dispatch, navigate, t, ToastError, currentPage]
	);
	// #endregion

	// #region DOWNLOAD PDF
	// Same as the Pending Tasks page: asks the backend for a short-lived (60 s) signed link and starts
	// the download right away. The backend checks download_pdf on the document's workflow first.
	const downloadDocument = useCallback(
		async (documentId) => {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			try {
				const result = await dispatch(fetchDocumentDownloadThunk({ documentId })).unwrap();
				downloadFromUrl(result?.data?.url);
			} catch (error) {
				logger.error("Error downloading document:", error);
				ToastError(getErrorMessage(error, t("Failed to download the document")));
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[dispatch, t, ToastError]
	);

	// The process's documents so far (e.g. one report per meeting): none ready -> a message; one ->
	// downloaded directly; several -> a popup to pick one.
	const handleDownloadPdf = useCallback(
		async (row) => {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			let readyDocuments = [];
			try {
				const result = await dispatch(fetchProcessDocumentsThunk({ processInstanceId: row.process_instance_id })).unwrap();
				readyDocuments = (result?.data || [])
					.map(mapProcessDocumentResponse)
					.filter((document) => document.status === "ready");
			} catch (error) {
				logger.error("Error fetching process documents:", error);
				ToastError(getErrorMessage(error, t("Failed to load the documents")));
				return;
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}

			if (readyDocuments.length === 0) {
				ToastInfo(t("No document has been generated for this process yet"));
			} else if (readyDocuments.length === 1) {
				await downloadDocument(readyDocuments[0].documentId);
			} else {
				dispatchState({ type: "OPEN_DOCUMENTS_DIALOG", payload: readyDocuments });
			}
		},
		[dispatch, t, ToastError, ToastInfo, downloadDocument]
	);

	const handleCloseDocumentsDialog = () => {
		dispatchState({ type: "CLOSE_DOCUMENTS_DIALOG" });
	};

	const handleConfirmDocumentsDialog = (data) => {
		dispatchState({ type: "CLOSE_DOCUMENTS_DIALOG" });
		if (data?.documentId) {
			downloadDocument(data.documentId);
		}
	};

	// One radio option per document, newest last. The date and version tell apart documents with the
	// same name (e.g. several "Meeting report", or one regenerated).
	const documentFields = useMemo(() => [
		{
			name: "documentId",
			type: "radio",
			row: false,
			required: true,
			options: state.dialogs.documents.map((document) => {
				const date = document.versionCreatedAt || document.createdAt;
				const details = [date ? new Date(date).toLocaleString() : null, document.version > 1 ? `${t("version")} ${document.version}` : null]
					.filter(Boolean)
					.join(", ");
				return { value: document.documentId, label: details ? `${t(document.displayName)} (${details})` : t(document.displayName) };
			}),
		},
	], [state.dialogs.documents, t]);

	// Newest document preselected.
	const documentDefaults = useMemo(() => ({
		documentId: state.dialogs.documents[state.dialogs.documents.length - 1]?.documentId ?? "",
	}), [state.dialogs.documents]);
	// #endregion

	// #region WORKFLOW HISTORY TABLE COLUMNS
	const actions = useMemo(() => ({
		onView: (row) => handleView(row.process_instance_id),
		onDelete: handleDeleteClick,
		onDownload: handleDownloadPdf,
		onReassign: handleReassignClick,
		onRegenerate: handleRegenerateClick,
		onClone: handleCloneClick,
	}), [handleView, handleDeleteClick, handleDownloadPdf, handleReassignClick, handleRegenerateClick, handleCloneClick]);

	// Button rules in tbl_table_column_configs use "hasWorkflowPermission", which calls
	// canUse(permissionCode, row.process_definition_key).
	const { canUse } = useWorkflowAccess();
	const context = useMemo(() => ({ canUse }), [canUse]);

	const { columns, isLoading: columnsLoading } = useTableColumns('WorkflowHistory', { actions, context });
	// #endregion

	// #region RETURN  COMPONENT
	if (error && !displayData?.length) {
		return <Alert severity="error">{error}</Alert>;
	}

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
				<Typography variant="h3" color="primary">
					{t("Workflow History")}
				</Typography>
				{isBackgroundRefreshing && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Loader variant="circular" message="Loading..." size="small" />
					</Box>
				)}
			</Box>

			<Typography variant="body1" color="text.secondary" paragraph>
				{t("Track and review the history of all completed and ongoing workflow processes.")}
			</Typography>

			<Divider sx={{ my: 3 }} />

			{/* Filters */}
			<Box
				sx={{
					my: 2,
					p: 2,
					borderRadius: 2,
					bgcolor: "rgba(29,131,84,0.04)",
				}}
			>
				<SearchFilterBar
					mode="backend"
					searchValue={state.table.searchInput}
					onSearchChange={(v) => dispatchState({ type: "SET_SEARCH_INPUT", payload: v })}
					showDateRange
					startDateValue={state.table.startDateInput}
					endDateValue={state.table.endDateInput}
					onStartDateChange={(v) => dispatchState({ type: "SET_START_DATE_INPUT", payload: v })}
					onEndDateChange={(v) => dispatchState({ type: "SET_END_DATE_INPUT", payload: v })}
					showActions
					onApply={applyFilters}
					onClear={clearFilters}
					showFilterButton={false}
					showSearchButton={false}
				/>
			</Box>
			{/* First load: header and filters stay; only the results area (total, table, paging) waits. */}
			{isFirstLoad || columnsLoading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<Loader variant="dots" message="Loading." />
				</Box>
			) : (
			<>
			<Typography variant="body1" sx={{ mt: 1 }}>
				{t("Total History")}: {totalCount}
			</Typography>

			{displayData.length === 0 ? (
				<DataContainer data={[]} emptyStateIcon={CalendarIcon} emptyStateTitle="No History Tasks yet" emptyStateSubtitle="Wait for someone to start or complete a task that you are involved in" />
			) : (
				<Box sx={{ mt: 2 }}>
					<DataContainer
						data={displayData}
						columns={columns} columnsLoading={columnsLoading}
						onSort={handleSort}
						sortConfig={{ key: state.table.sortField, direction: state.table.sortDirection }}
						showRowNumbers={true}
						maxHeight={600}
						stickyHeader={true}
					/>
				</Box>
			)}
			<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
				<Stack direction="row" spacing={2} alignItems="center">
					<TextField
						select
						size="small"
						label={t("Per page")}
						value={recordsPerPage}
						onChange={handleChangeRecordsPerPage}
						sx={{
							minWidth: 120,
							"& .MuiOutlinedInput-root": {
								"& fieldset": { borderColor: "primary.main" },
								"&:hover fieldset": { borderColor: "primary.main" },
								"&.Mui-focused fieldset": { borderColor: "primary.main" },
							},
						}}
					>
						{[3, 5, 10, 15].map((n) => (
							<MenuItem key={n} value={n}>
								{n}
							</MenuItem>
						))}
					</TextField>

					<CustomPagination
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={handlePageChange}
						color="primary"
						showFirstButton
						showLastButton
						sx={{ mt: 2 }}
					/>
				</Stack>
			</Box>
			</>
			)}
			{/* Delete Confirmation Dialog */}
			{/* <ConfirmationDialog
                open={deleteConfirmOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Confirm Delete"
                message="Are you sure you want to delete this workflow history record? This action cannot be undone."
                cancelText="Cancel"
                confirmText="Delete"
                confirmColor="error"
            /> */}
			<EnhancedConfirmationDialog
				open={state.dialogs.deleteConfirm}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title={t("Confirm Delete")}
				message={t(em.areyousuredelete)}
				confirmText={t("DELETE")}
				confirmColor="error"
				cancelText={t("Cancel")}
				showMessage={true}
			/>
			<EnhancedConfirmationDialog
				open={state.dialogs.cloneConfirm}
				onClose={handleCancelClone}
				onConfirm={handleConfirmClone}
				title="Confirm Clone"
				message="Are you sure you want to clone this workflow?"
				confirmText="CLONE"
				confirmColor="success"
				cancelText="Cancel"
			/>
			{/* <ConfirmationDialog
                open={acceptConfirmOpen}
                onClose={handleCancel}
                onConfirm={handleAccept}
                title="Confirm Acceptance"
                message="Are you sure you want to accept this request?"
                confirmText="Accept"
                confirmColor="success"
            /> */}
			{/* <ConfirmationDialog
                open={submitConfirmOpen}
                onClose={handleCancel}
                onConfirm={handleSubmit}
                title="Confirm Submission"
                message="Once submitted, you cannot edit this form. Do you want to proceed?"
                confirmText="Submit"
                confirmColor="primary"
            /> */}
			{/* <ConfirmationDialog
                open={confirmOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Warning"
                message="This action may have consequences. Are you sure?"
                confirmText="Proceed"
                confirmColor="warning"
            /> */}

			<EnhancedConfirmationDialog
				open={state.dialogs.reassignOpen}
				onClose={handleCancelReassign}
				onConfirm={handleConfirmReassign}
				title={t("Reassign Task")}
				message={`${t("Choose who will work on this task")}: ${t(state.dialogs.reassign?.taskName ?? "")}`}
				fields={reassignFields}
				defaultValues={reassignDefaults}
				confirmText={t("Reassign")}
				cancelText={t("Cancel")}
				confirmColor="primary"
				showMessage={true}
			/>

			<EnhancedConfirmationDialog
				open={state.dialogs.documentsOpen}
				onClose={handleCloseDocumentsDialog}
				onConfirm={handleConfirmDocumentsDialog}
				title={t("Download Document")}
				message={t("This process has several documents. Choose the one to download.")}
				fields={documentFields}
				defaultValues={documentDefaults}
				confirmText={t("Download")}
				cancelText={t("Cancel")}
				showMessage={true}
			/>

			<EnhancedConfirmationDialog
				open={state.dialogs.regenerateOpen}
				onClose={handleCancelRegenerate}
				onConfirm={handleConfirmRegenerate}
				title={t("Regenerate Document")}
				message={t("Choose the document to regenerate.")}
				fields={regenerateFields}
				defaultValues={regenerateDefaults}
				confirmText={t("Next")}
				cancelText={t("Cancel")}
				confirmColor="primary"
				showMessage={true}
			/>

			<EnhancedConfirmationDialog
				open={state.dialogs.regenerateVersionOpen}
				onClose={handleCancelRegenerateVersion}
				onConfirm={handleConfirmRegenerateVersion}
				title={t("Regenerate Document")}
				message={t("The document is rendered again with the chosen template version, as a new version. The current version is kept.")}
				fields={regenerateVersionFields}
				defaultValues={regenerateVersionDefaults}
				confirmText={t("Regenerate")}
				cancelText={t("Cancel")}
				confirmColor="primary"
				showMessage={true}
			/>
			{/* <LoadingBackdrop
                open={isLoadingBackdrop}
                message="Loading data, please wait..."
            /> */}
			<LoadingBackdrop open={state.loading.isLoadingBackdrop} message="Loading data, please wait..." variant="bars" color="greenGradient" />
		</Box>
	);
	// #endregion
}
